/**
 * ARTWORK — IS THIS CANVA ENTRY A FOLDER? (pure, zero imports)
 *
 * ONE place decides folder-vs-design, because the two normalizers that used to
 * decide it separately DID drift, and the drift is what broke drill-down on the
 * live site.
 *
 * THE BUG THIS EXISTS TO KILL. The live route is
 * `sr-portal-backend/canva.js#listFolder`, and it already flattens Canva's own
 * envelope before the client ever sees it:
 *
 *     folders = d.items.filter(x => x.type==='folder' && x.folder)
 *                      .map(x => ({ id: x.folder.id, name: ..., isFolder: true }))
 *     designs = d.items.filter(x => x.type==='design').map(mapDesign)
 *     return { items: [...folders, ...designs], continuation }
 *
 * So a folder reaches the client as **`{ id, name, isFolder: true }`** — with NO
 * `type` field and NO nested `entry.folder`. The engine normalizer tested only
 * `/folder/i.test(entry.type) || (entry.folder && !entry.design)`, so every
 * folder came back `isFolder: false`, was emitted as a DESIGN, and rendered as a
 * tile that selects instead of opening. A folder whose designs live one level
 * down therefore showed exactly ONE undrillable tile — the sub-folder — and
 * there was no way in. The studio normalizer read the flag; the engine one did
 * not. Hence: one function, both callers.
 *
 * Recognized, in order of authority:
 *   1. `isFolder` — the live backend's own flag, and the answer when present
 *   2. `type` / `item_type` / `itemType` / `kind` — Canva's discriminator, which
 *      has shipped as both `folder` and `FOLDER`, so it is matched
 *      case-insensitively rather than by `=== 'folder'`
 *   3. a nested `entry.folder` with no `entry.design` — Canva's raw envelope
 *
 * Anything else is a design/image, which is the safe default: mis-reading a
 * design as a folder would hide a real, pickable component behind a drill-in
 * that leads nowhere, while mis-reading a folder as a design is the failure we
 * just shipped.
 */

/** Does this entry represent a FOLDER (drill into it) rather than a design? */
export function isFolderEntry(entry) {
  if (!entry || typeof entry !== 'object') return false

  // The backend states it outright. Believe it — in both directions, so an
  // explicit `isFolder: false` is never second-guessed by a stray `type`.
  if (entry.isFolder === true) return true
  if (entry.isFolder === false) return false

  const type = String(entry.type || entry.item_type || entry.itemType || entry.kind || '').trim()
  // Case-insensitive: Canva has answered with both `folder` and `FOLDER`.
  if (/folder/i.test(type)) return !/design|image|asset/i.test(type)

  // Canva's raw envelope: { type, folder: {...} } vs { type, design: {...} }.
  return !!entry.folder && !entry.design
}

/**
 * The entry's id, wherever it lives. The backend flattens it onto `id`; Canva's
 * raw envelope nests it under `folder` / `design`. An entry with no resolvable
 * id is dropped by the callers — a tile with no id cannot be picked or opened,
 * and React would key it as `undefined`.
 */
export function entryId(entry) {
  if (!entry || typeof entry !== 'object') return null
  const nested = entry.folder || entry.design || entry.image || entry.asset || null
  const id =
    entry.id ||
    entry.folderId ||
    entry.designId ||
    (nested && (nested.id || nested.folderId || nested.designId)) ||
    entry.key ||
    null
  return id ? String(id) : null
}
